/* eslint-disable perfectionist/sort-objects -- Keep translation locale columns in APP_LOCALES order. */
import { CATALOG_LOCALES, CatalogLocale } from '../locale';
import { defineDomainTranslations } from './defineDomainTranslations';

type TranslationRow = Record<CatalogLocale, string>;

const SHELL_TRANSLATION_ROWS = {
  'common.close': { en: 'Close', es: 'Cerrar', pt: 'Fechar', fr: 'Fermer', it: 'Chiudi', ru: 'Закрыть' },
  'common.upgrade': {
    en: 'Upgrade',
    es: 'Mejorar el plan',
    pt: 'Fazer upgrade',
    fr: 'Mettre à niveau',
    it: 'Fai l’upgrade',
    ru: 'Улучшить тариф',
  },
  'sidebar.api.getKey': {
    en: 'Get API key',
    es: 'Obtener clave de API',
    pt: 'Obter chave da API',
    fr: 'Obtenir une clé API',
    it: 'Ottieni la chiave API',
    ru: 'Получить API-ключ',
  },
  'sidebar.api.title': {
    en: 'Use Rask API across your products',
    es: 'Usa la API de Rask en tus productos',
    pt: 'Use a API da Rask em seus produtos',
    fr: 'Utilisez l’API Rask dans vos produits',
    it: 'Usa l’API di Rask nei tuoi prodotti',
    ru: 'Используйте API Rask в своих продуктах',
  },
  'sidebar.teamspace.defaultName': {
    en: 'My Teamspace',
    es: 'Mi Teamspace',
    pt: 'Meu Teamspace',
    fr: 'Mon Teamspace',
    it: 'Il mio Teamspace',
    ru: 'Мой Teamspace',
  },
  'sidebar.teamspace.description': {
    en: 'Make teamwork easier with Teamspaces',
    es: 'Facilita el trabajo en equipo con Teamspaces',
    pt: 'Facilite o trabalho em equipe com os Teamspaces',
    fr: 'Simplifiez le travail d’équipe avec les Teamspaces',
    it: 'Semplifica il lavoro di squadra con i Teamspace',
    ru: 'Упростите командную работу с Teamspace',
  },
  'sidebar.teamspace.new': {
    en: 'New Teamspace',
    es: 'Nuevo Teamspace',
    pt: 'Novo Teamspace',
    fr: 'Nouveau Teamspace',
    it: 'Nuovo Teamspace',
    ru: 'Новый Teamspace',
  },
  'teamspace.action.cancel': {
    en: 'Cancel',
    es: 'Cancelar',
    pt: 'Cancelar',
    fr: 'Annuler',
    it: 'Annulla',
    ru: 'Отмена',
  },
  'teamspace.action.delete': {
    en: 'Delete',
    es: 'Eliminar',
    pt: 'Excluir',
    fr: 'Supprimer',
    it: 'Elimina',
    ru: 'Удалить',
  },
  'teamspace.action.discardChanges': {
    en: 'Discard changes',
    es: 'Descartar cambios',
    pt: 'Descartar alterações',
    fr: 'Ignorer les modifications',
    it: 'Scarta modifiche',
    ru: 'Отменить изменения',
  },
  'teamspace.action.leave': {
    en: 'Leave',
    es: 'Salir',
    pt: 'Sair',
    fr: 'Quitter',
    it: 'Lascia',
    ru: 'Покинуть',
  },
  'teamspace.action.remove': {
    en: 'Remove',
    es: 'Eliminar',
    pt: 'Remover',
    fr: 'Retirer',
    it: 'Rimuovi',
    ru: 'Удалить',
  },
  'teamspace.action.save': {
    en: 'Save',
    es: 'Guardar',
    pt: 'Salvar',
    fr: 'Enregistrer',
    it: 'Salva',
    ru: 'Сохранить',
  },
  'teamspace.delete.subtitle': {
    en: 'Once you delete it, you and your team will permanently lose access to all projects in this Teamspace.',
    es: 'Una vez eliminado, tú y tu equipo perderán permanentemente el acceso a todos los proyectos de este Teamspace.',
    pt: 'Depois de excluí-lo, você e sua equipe perderão permanentemente o acesso a todos os projetos deste Teamspace.',
    fr: 'Après sa suppression, votre équipe et vous perdrez définitivement l’accès à tous les projets de ce Teamspace.',
    it: 'Dopo averlo eliminato, tu e il tuo team perderete definitivamente l’accesso a tutti i progetti di questo Teamspace.',
    ru: 'После удаления вы и ваша команда навсегда потеряете доступ ко всем проектам в этом Teamspace.',
  },
  'teamspace.delete.title': {
    en: 'Would you like to permanently delete this Teamspace?',
    es: '¿Quieres eliminar permanentemente este Teamspace?',
    pt: 'Deseja excluir permanentemente este Teamspace?',
    fr: 'Souhaitez-vous supprimer définitivement ce Teamspace ?',
    it: 'Vuoi eliminare definitivamente questo Teamspace?',
    ru: 'Удалить этот Teamspace навсегда?',
  },
  'teamspace.discard.subtitle': {
    en: 'If you exit now, your changes will be lost.',
    es: 'Si sales ahora, se perderán los cambios.',
    pt: 'Se você sair agora, suas alterações serão perdidas.',
    fr: 'Si vous quittez maintenant, vos modifications seront perdues.',
    it: 'Se esci ora, le modifiche andranno perse.',
    ru: 'Если выйти сейчас, изменения будут потеряны.',
  },
  'teamspace.discard.title': {
    en: 'You have unsaved changes',
    es: 'Tienes cambios sin guardar',
    pt: 'Você tem alterações não salvas',
    fr: 'Vous avez des modifications non enregistrées',
    it: 'Hai modifiche non salvate',
    ru: 'У вас есть несохранённые изменения',
  },
  'teamspace.form.color': {
    en: 'Color',
    es: 'Color',
    pt: 'Cor',
    fr: 'Couleur',
    it: 'Colore',
    ru: 'Цвет',
  },
  'teamspace.form.leave': {
    en: 'Leave Teamspace',
    es: 'Salir del Teamspace',
    pt: 'Sair do Teamspace',
    fr: 'Quitter le Teamspace',
    it: 'Lascia il Teamspace',
    ru: 'Покинуть Teamspace',
  },
  'teamspace.form.name': {
    en: 'Teamspace name',
    es: 'Nombre del Teamspace',
    pt: 'Nome do Teamspace',
    fr: 'Nom du Teamspace',
    it: 'Nome del Teamspace',
    ru: 'Название Teamspace',
  },
  'teamspace.form.namePlaceholder': {
    en: 'Enter name',
    es: 'Introduce un nombre',
    pt: 'Digite o nome',
    fr: 'Saisissez un nom',
    it: 'Inserisci un nome',
    ru: 'Введите название',
  },
  'teamspace.leave.subtitle': {
    en: 'Once you leave, you will lose access to all projects in this space.',
    es: 'Cuando salgas, perderás el acceso a todos los proyectos de este espacio.',
    pt: 'Depois de sair, você perderá o acesso a todos os projetos deste espaço.',
    fr: 'Après votre départ, vous perdrez l’accès à tous les projets de cet espace.',
    it: 'Dopo aver lasciato questo spazio, perderai l’accesso a tutti i suoi progetti.',
    ru: 'После выхода вы потеряете доступ ко всем проектам в этом пространстве.',
  },
  'teamspace.leave.title': {
    en: 'Would you like to leave this space?',
    es: '¿Quieres salir de este espacio?',
    pt: 'Deseja sair deste espaço?',
    fr: 'Souhaitez-vous quitter cet espace ?',
    it: 'Vuoi lasciare questo spazio?',
    ru: 'Покинуть это пространство?',
  },
  'teamspace.member.count': {
    en: 'Members {count} / {limit}',
    es: 'Miembros {count} / {limit}',
    pt: 'Membros {count} / {limit}',
    fr: 'Membres {count} / {limit}',
    it: 'Membri {count} / {limit}',
    ru: 'Участники {count} / {limit}',
  },
  'teamspace.member.duplicateEmail': {
    en: 'This email is already added',
    es: 'Este correo electrónico ya está añadido',
    pt: 'Este e-mail já foi adicionado',
    fr: 'Cette adresse e-mail a déjà été ajoutée',
    it: 'Questa e-mail è già stata aggiunta',
    ru: 'Этот адрес уже добавлен',
  },
  'teamspace.member.editor': {
    en: 'Editor',
    es: 'Editor',
    pt: 'Editor',
    fr: 'Éditeur',
    it: 'Editor',
    ru: 'Редактор',
  },
  'teamspace.member.emailPlaceholder': {
    en: 'Enter email',
    es: 'Introduce el correo electrónico',
    pt: 'Digite o e-mail',
    fr: 'Saisissez une adresse e-mail',
    it: 'Inserisci l’e-mail',
    ru: 'Введите адрес электронной почты',
  },
  'teamspace.member.incorrectEmail': {
    en: 'Enter a valid email address',
    es: 'Correo electrónico incorrecto',
    pt: 'E-mail incorreto',
    fr: 'Adresse e-mail incorrecte',
    it: 'E-mail non valida',
    ru: 'Некорректный адрес электронной почты',
  },
  'teamspace.member.invite': {
    en: 'Invite',
    es: 'Invitar',
    pt: 'Convidar',
    fr: 'Inviter',
    it: 'Invita',
    ru: 'Пригласить',
  },
  'teamspace.member.owner': {
    en: 'Owner',
    es: 'Propietario',
    pt: 'Proprietário',
    fr: 'Propriétaire',
    it: 'Proprietario',
    ru: 'Владелец',
  },
  'teamspace.member.ownerWithMembers': {
    en: 'Owner • Members: {count}',
    es: 'Propietario • Miembros: {count}',
    pt: 'Proprietário • Membros: {count}',
    fr: 'Propriétaire • Membres : {count}',
    it: 'Proprietario • Membri: {count}',
    ru: 'Владелец • Участники: {count}',
  },
  'teamspace.member.remove.subtitle': {
    en: 'Once removed, they will lose access to the Teamspace and all projects within it. You can re-add the member later and restore their access.',
    es: 'Una vez eliminado, perderá el acceso al Teamspace y a todos sus proyectos. Puedes volver a añadirlo más adelante para restaurar su acceso.',
    pt: 'Depois de removido, perderá o acesso ao Teamspace e a todos os projetos nele. Você poderá adicionar o membro novamente mais tarde e restaurar o acesso.',
    fr: 'Une fois retiré, ce membre perdra l’accès au Teamspace et à tous ses projets. Vous pourrez l’ajouter à nouveau ultérieurement pour rétablir son accès.',
    it: 'Dopo la rimozione, questa persona perderà l’accesso al Teamspace e a tutti i progetti al suo interno. Potrai aggiungerla di nuovo in seguito e ripristinarne l’accesso.',
    ru: 'После удаления участник потеряет доступ к Teamspace и всем проектам в нём. Позже его можно будет добавить снова и восстановить доступ.',
  },
  'teamspace.member.remove.title': {
    en: 'Are you sure you want to remove this member from the Teamspace?',
    es: '¿Seguro que quieres eliminar a este miembro del Teamspace?',
    pt: 'Tem certeza de que deseja remover este membro do Teamspace?',
    fr: 'Voulez-vous vraiment retirer ce membre du Teamspace ?',
    it: 'Vuoi davvero rimuovere questa persona dal Teamspace?',
    ru: 'Удалить этого участника из Teamspace?',
  },
  'teamspace.member.you': {
    en: '(you)',
    es: '(tú)',
    pt: '(você)',
    fr: '(vous)',
    it: '(tu)',
    ru: '(вы)',
  },
  'teamspace.settings.fallbackName': {
    en: 'Teamspace',
    es: 'Teamspace',
    pt: 'Teamspace',
    fr: 'Teamspace',
    it: 'Teamspace',
    ru: 'Teamspace',
  },
  'teamspace.settings.tab': {
    en: 'Settings',
    es: 'Configuración',
    pt: 'Configurações',
    fr: 'Paramètres',
    it: 'Impostazioni',
    ru: 'Настройки',
  },
  'teamspace.settings.usageTab': {
    en: 'Usage',
    es: 'Uso',
    pt: 'Uso',
    fr: 'Utilisation',
    it: 'Utilizzo',
    ru: 'Использование',
  },
  'teamspace.upgrade.addMore': {
    en: 'Add more',
    es: 'Añadir más',
    pt: 'Adicionar mais',
    fr: 'En ajouter',
    it: 'Aggiungi',
    ru: 'Добавить места',
  },
  'teamspace.upgrade.getStarted': {
    en: 'Get Started',
    es: 'Empezar',
    pt: 'Começar',
    fr: 'Commencer',
    it: 'Inizia',
    ru: 'Начать',
  },
  'teamspace.upgrade.inviteUsers': {
    en: 'Invite users to collaborate',
    es: 'Invita a usuarios para colaborar',
    pt: 'Convide usuários para colaborar',
    fr: 'Invitez des utilisateurs à collaborer',
    it: 'Invita utenti a collaborare',
    ru: 'Пригласите пользователей для совместной работы',
  },
  'teamspace.upgrade.planMembers': {
    en: 'Member limit on your plan: {count}',
    es: 'Límite de miembros de tu plan: {count}',
    pt: 'Limite de membros do seu plano: {count}',
    fr: 'Nombre de membres autorisé par votre offre : {count}',
    it: 'Il tuo piano include fino a {count} membri',
    ru: 'Лимит участников по тарифу: {count}',
  },
  'teamspace.usage.description': {
    en: 'These minutes are shared across all Teamspaces you own.',
    es: 'Estos minutos se comparten entre todos los Teamspaces que posees.',
    pt: 'Estes minutos são partilhados entre todos os Teamspaces dos quais é proprietário.',
    fr: 'Ces minutes sont partagées entre tous les Teamspaces dont vous êtes propriétaire.',
    it: 'Questi minuti sono condivisi tra tutti i Teamspace di tua proprietà.',
    ru: 'Эти минуты доступны из общего баланса во всех пространствах, которыми вы владеете.',
  },
  'teamspace.usage.manageSubscription': {
    en: 'Manage subscription',
    es: 'Gestionar suscripción',
    pt: 'Gerenciar assinatura',
    fr: 'Gérer l’abonnement',
    it: 'Gestisci abbonamento',
    ru: 'Управлять подпиской',
  },
  'teamspace.usage.minutes': {
    en: 'Teamspace minutes',
    es: 'Minutos del Teamspace',
    pt: 'Minutos do Teamspace',
    fr: 'Minutes du Teamspace',
    it: 'Minuti del Teamspace',
    ru: 'Минуты Teamspace',
  },
  'teamspace.welcome.createFirst': {
    en: 'Get started by creating your first video in the Translation tab, then follow the onboarding steps.',
    es: 'Empieza creando tu primer video en la pestaña Traducción y sigue los pasos de incorporación.',
    pt: 'Comece criando seu primeiro vídeo na aba Tradução e siga as etapas de integração.',
    fr: 'Commencez par créer votre première vidéo dans l’onglet Traduction, puis suivez les étapes de prise en main.',
    it: 'Inizia creando il tuo primo video nella scheda Traduzione, quindi segui i passaggi introduttivi.',
    ru: 'Создайте первое видео на вкладке «Перевод», а затем пройдите этапы знакомства с платформой.',
  },
  'teamspace.welcome.getStarted': {
    en: 'Get started',
    es: 'Empezar',
    pt: 'Começar',
    fr: 'Commencer',
    it: 'Inizia',
    ru: 'Начать',
  },
  'teamspace.welcome.invited': {
    en: 'You’ve been invited to collaborate with your team on video localization projects.',
    es: 'Te han invitado a colaborar con tu equipo en proyectos de adaptación de video.',
    pt: 'Você foi convidado para colaborar com sua equipe em projetos de adaptação de vídeo.',
    fr: 'Votre équipe vous invite à collaborer sur des projets d’adaptation vidéo.',
    it: 'Hai ricevuto un invito a collaborare con il tuo team a progetti di localizzazione video.',
    ru: 'Вас пригласили работать с командой над проектами локализации видео.',
  },
  'teamspace.welcome.title': {
    en: 'Welcome to {name} Teamspace!',
    es: '¡Te damos la bienvenida al Teamspace {name}!',
    pt: 'Bem-vindo ao Teamspace {name}!',
    fr: 'Bienvenue dans le Teamspace {name} !',
    it: 'Ti diamo il benvenuto nel Teamspace {name}!',
    ru: 'Добро пожаловать в Teamspace {name}!',
  },
} satisfies Record<string, TranslationRow>;

type ShellTranslationKey = keyof typeof SHELL_TRANSLATION_ROWS;
type ShellDictionaries = Record<CatalogLocale, Record<ShellTranslationKey, string>>;

const dictionaries = Object.fromEntries(
  CATALOG_LOCALES.map((locale) => [
    locale,
    Object.fromEntries(
      Object.entries(SHELL_TRANSLATION_ROWS).map(([key, translations]) => [key, translations[locale]]),
    ),
  ]),
) as ShellDictionaries;

export const SHELL_TRANSLATIONS = defineDomainTranslations(dictionaries);
